/**
 * CustomAtividades.tsx — o registro do que foi feito (migration 088).
 *
 * O chat é para as pessoas conversarem. Isto é outra coisa: o registro do
 * TRABALHO — quem fez o quê, quando, e por quê. Dele, 05/09/2026, recusando a
 * ideia de reusar a sala do chat: *"o chat não é para isso; eu não sei nem se
 * ele vai ser usado"*. O registro tem de existir mesmo sem chat nenhum.
 *
 * Serve aos dois donos com o mesmo componente:
 *   - a ETAPA (`etapaId`), na COLUNA da página dela — não numa aba ao lado
 *     do chat, que foi recusado por parecerem a mesma coisa;
 *   - a AUTORIZAÇÃO inteira (só `autorizacaoId`), onde a linha do tempo junta o
 *     que veio das etapas — e é o único lugar do caso simples, que nunca teve
 *     etapa.
 *
 * ⚠️ A lista é IMUTÁVEL: não há editar nem apagar, e não deve haver. Se
 * aparecer um lápis aqui, o desenho foi quebrado — errou-se, grava-se outra
 * linha corrigindo.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/auth/auth-context';
import { api, ApiError } from '@/lib/api';
import { comprimirImagem } from '@/lib/imagem';
import { tempoRelativo, formatarData } from '@/lib/data';
import { numerosDasEtapas } from './CustomEtapasLista';
import type { Etapa } from './CustomEtapas';
import type { Documento } from './CustomDocumentos';

export interface Atividade {
  id: number;
  autorizacao_id: number;
  etapa_id: number | null;
  etapa_titulo: string | null;
  tipo: string;
  relato: string | null;
  prazo_anterior: string | null;
  prazo_novo: string | null;
  criacao: string | null;
  /** Quando o relato foi corrigido. Preenchido = a tela mostra "editado". */
  atualizacao?: string | null;
  autor_id: number;
  autor_nome: string;
  autor_foto: string | null;
}

/**
 * ⛔ REGISTRO É O QUE ALGUÉM ESCREVEU. O RESTO É HISTÓRICO.
 *
 * Dele, 05/09/2026, e é a SEGUNDA vez que ele corrige isto:
 *
 *   "Você aos poucos foi transformando as atividades em log. 'Vittorio criou a
 *   etapa', 'Vittorio assumiu', 'Vittorio reabriu'. A ideia era registrar que
 *   você entrou em contato com o cliente, que está aguardando, que recebeu um
 *   documento. Essas criadas pelo sistema você não mostra — elas ficam
 *   escondidas."
 *
 * O critério é o dele, e cabe numa linha: **a linha aparece se tem texto de
 * gente**. Não é o tipo que decide — é se alguém escreveu.
 *
 *   - `relato` é sempre texto de gente;
 *   - `adiamento` traz o MOTIVO, que é obrigatório desde 05/09 — é registro;
 *   - `conclusao` traz a observação do desfecho, quando há — aí é registro;
 *     sem observação, é só o carimbo de que fechou, e vai para o histórico.
 *   - `criacao`, `inicio`, `edicao`, `responsavel`, `reabertura` são o log, e
 *     ele continua guardado (serve de auditoria) — só não ocupa a tela de quem
 *     quer saber o que aconteceu.
 *
 * O histórico não some: fica atrás de um interruptor, para quem for auditar.
 *
 * ⚠️ **A PERGUNTA É PELO TIPO, e não só "tem texto?".** Era `Boolean(relato)`, e
 * isso quebrou em 12/09, quando o carimbo de troca de responsável passou a
 * gravar o nome de quem recebeu ("passou a bola PARA A PATRÍCIA"). Com a conta
 * antiga, delegar uma etapa jogaria o carimbo para a linha do tempo principal —
 * que é exatamente o erro que ele corrigiu duas vezes em 05/09: *registro de
 * atividade não é log*. O texto serve para o carimbo dizer mais, não para ele
 * mudar de lugar.
 */
const TIPOS_DE_GENTE = new Set(['relato', 'adiamento', 'conclusao']);

export const eRegistroDeGente = (a: Atividade): boolean =>
  TIPOS_DE_GENTE.has(a.tipo) && Boolean((a.relato ?? '').trim());

/** O que cada tipo diz. Palavra, não ícone: quem lê a linha do tempo não
 *  conhece o vocabulário de símbolos da tela, e "adiou o prazo" não se
 *  confunde com nada. */
const VOCABULARIO: Record<string, string> = {
  relato: 'registrou',
  /* ⚠️ `inicio` FALTAVA — o tipo nasceu com a 091 e o rótulo não veio junto, e
     a tela escrevia "Vittorio Lo Bianco inicio há 2 h", em cru. Tipo novo sem
     rótulo não quebra nada: só fica feio, e por isso ninguém acha. */
  /* "assumiu" saiu do vocabulário em 12/09, junto com o botão Assumir: pôr em
     andamento virou mudança de campo, e ninguém assume nada. */
  inicio: 'pôs em andamento',
  adiamento: 'adiou o prazo',
  conclusao: 'concluiu',
  reabertura: 'reabriu',
  responsavel: 'passou a bola',
  criacao: 'criou a etapa',
  edicao: 'editou',
  decisao: 'decidiu o pedido',
  encerramento: 'encerrou o ciclo',
};

interface Props {
  autorizacaoId: number;
  /** Sem etapa, a lista é a do PEDIDO inteiro — incluindo o que veio das etapas. */
  etapaId?: number;
  /** Quem não pode gerir só lê. */
  podeEscrever?: boolean;
}

/* ⛔ ONDE SE REGISTRA, E ONDE SÓ SE LÊ — pergunta dele em 05/09: *"como eu vou
   registrar uma atividade dentro da autorização, se a gente combinou que só a
   etapa pode ter atividade?"*

   A autorização PODE registrar — foi decidido no plano, para o pedido simples
   que nunca tem etapa ("o Welliton pede, a Adriana responde, ele anexa a
   nota"). Mas ele tem razão no que incomoda: **quando existem etapas, o
   trabalho acontece nelas**, e um campo solto na visão do pedido convida a
   registrar no lugar errado — a atividade nasceria órfã de etapa, e ninguém
   saberia a que parte do caso ela pertence.

   Por isso: **com etapas, a visão do pedido é só leitura.** Sem nenhuma etapa,
   o campo aparece — é o único lugar que existe. */

/** UMA linha da série — o bloco de comentário do `ForumDetails.tsx`: `row gx-3
 *  mt-3` (é ele que dá o respiro), avatar 40 com sombra, nome e tempo. */
function Linha({ a, docs = [], historico = false, meuId, aoMudar }: {
  a: Atividade;
  /** A PROVA daquele registro — as fotos e PDFs anexados junto com ele. */
  docs?: Documento[];
  /** Linha de sistema: mesma estrutura, menos peso. */
  historico?: boolean;
  /** Quem está olhando — só o autor corrige e apaga o próprio registro. */
  meuId?: number | null;
  aoMudar?: () => void;
}) {
  const rotulo = VOCABULARIO[a.tipo] ?? a.tipo;
  const [editando, setEditando] = useState(false);
  const [rascunho, setRascunho] = useState(a.relato ?? '');
  const [salvando, setSalvando] = useState(false);

  /* ⛔ SÓ O REGISTRO MANUAL, E SÓ DE QUEM ESCREVEU. O log ninguém digitou; o
     motivo do adiamento e a observação da conclusão são parte do ato carimbado.
     A regra de verdade está no serviço — aqui só se esconde o que ia dar 403. */
  // `meuId` chega NULL quando a pessoa não pode corrigir — aí nada é "meu" para editar.
  const meu = a.tipo === 'relato' && meuId != null && a.autor_id === meuId;

  async function salvar() {
    const t = rascunho.trim();
    if (!t || salvando) return;
    setSalvando(true);
    try {
      await api.put(`/autorizacoes/atividades/${a.id}`, { relato: t });
      setEditando(false);
      aoMudar?.();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não deu para corrigir.');
    } finally {
      setSalvando(false);
    }
  }

  async function apagar() {
    try {
      await api.delete(`/autorizacoes/atividades/${a.id}`);
      aoMudar?.();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não deu para apagar.');
    }
  }
  if (historico) {
    /* O log é uma LINHA, não um bloco com rosto: quem abre o histórico quer
       varrer, não ler. */
    return (
      <p className="small text-secondary mb-1">
        <i className="bi bi-dot"></i>
        <span className="fw-medium">{a.autor_nome}</span> {rotulo}
        {/* O carimbo pode ter um COMPLEMENTO — "passou a bola PARA A PATRÍCIA".
            Sem isto a linha dizia só que houve troca, e quem lê meses depois
            tinha de deduzir para quem pelo responsável de hoje, que pode ter
            mudado de novo. Pergunta dele, 12/09. */}
        {a.relato && <span> {a.relato}</span>}
        {a.criacao && <span className="ms-2">{tempoRelativo(a.criacao)}</span>}
      </p>
    );
  }
  return (
    <div className="row gx-3 mt-3">
      <div className="col-auto">
        {a.autor_foto ? (
          <figure className="avatar avatar-40 rounded-circle casaa-foto-no-avatar vm shadow-sm mb-0">
            <img src={a.autor_foto} alt="" />
          </figure>
        ) : (
          <span className="avatar avatar-40 rounded-circle bg-theme-1-subtle text-theme-1 vm shadow-sm">
            <i className="bi bi-person"></i>
          </span>
        )}
      </div>
      <div className="col">
        <p className="mb-1">
          <span className="fw-semibold">{a.autor_nome}</span>
          <span className="text-secondary"> {rotulo}</span>
          {a.criacao && (
            <span className="text-secondary d-inline-block ms-2 small">
              {tempoRelativo(a.criacao)}
            </span>
          )}
          {/* CORRIGIR EM SILÊNCIO seria pior do que não corrigir: quem lê um
              registro precisa saber que ele mudou depois de escrito. */}
          {a.atualizacao && (
            <span className="text-secondary ms-2 small fst-italic">· editado</span>
          )}
          {meu && !editando && (
            <span className="ms-2">
              <button type="button" className="btn btn-link btn-sm p-0 text-secondary"
                      title="Corrigir este registro"
                      onClick={() => { setRascunho(a.relato ?? ''); setEditando(true); }}>
                <i className="bi bi-pencil"></i>
              </button>
              <button type="button" className="btn btn-link btn-sm p-0 ms-2 text-secondary"
                      title="Apagar este registro"
                      onClick={apagar}>
                <i className="bi bi-trash3"></i>
              </button>
            </span>
          )}
        </p>
        {editando ? (
          <div className="mb-2">
            <textarea
              className="form-control mb-2"
              rows={2}
              value={rascunho}
              onChange={(ev) => setRascunho(ev.target.value)}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter' && !ev.shiftKey && !ev.nativeEvent.isComposing) {
                  ev.preventDefault();
                  salvar();
                } else if (ev.key === 'Escape') {
                  setEditando(false);
                  setRascunho(a.relato ?? '');
                }
              }}
              maxLength={2000}
              autoFocus
            />
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-sm btn-theme"
                      onClick={salvar} disabled={salvando || !rascunho.trim()}>
                {salvando && <span className="spinner-border spinner-border-sm me-1" />}
                Salvar a correção
              </button>
              <button type="button" className="btn btn-sm btn-outline-secondary"
                      onClick={() => { setEditando(false); setRascunho(a.relato ?? ''); }}>
                Deixar como está
              </button>
            </div>
          </div>
        ) : (
          a.relato && <p className="mb-1">{a.relato}</p>
        )}
        {/* A PROVA junto do relato — dele, desde o começo: *"na hora que você
            vai registrar uma atividade, você inclui um documento"*. Solto na
            aba Documentos, o arquivo não tinha quem o explicasse: *"que porra
            de documento é esse?"*. Aqui o relato é a descrição. */}
        {docs.length > 0 && (
          <div className="d-flex flex-wrap gap-2 mb-1">
            {docs.map((d) => (
              <a key={d.id} href={d.url} target="_blank" rel="noreferrer"
                 className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1">
                <i className={`bi ${d.e_imagem ? 'bi-image' : 'bi-file-earmark-pdf'}`}></i>
                <span className="text-truncate" style={{ maxWidth: '12rem' }}>
                  {d.nome ?? 'documento'}
                </span>
              </a>
            ))}
          </div>
        )}
        {/* O adiamento mostra de quando para quando — é o que a pessoa quer
            saber ao ver "adiou pela terceira vez". */}
        {a.tipo === 'adiamento' && a.prazo_novo && (
          <p className="small text-secondary mb-0">
            <i className="bi bi-arrow-right me-1"></i>
            {a.prazo_anterior ? `${formatarData(a.prazo_anterior)} → ` : 'sem prazo → '}
            {formatarData(a.prazo_novo)}
          </p>
        )}
      </div>
    </div>
  );
}

export function CustomAtividades({ autorizacaoId, etapaId, podeEscrever }: Props) {
  const { usuario, pode } = useAuth();
  const meuId = usuario?.id ?? null;
  // Corrigir e apagar o próprio registro passam por rotas que pedem
  // `autorizacoes.etapas.gerir` — sem ela o lápis e a lixeira davam 403 (16/09).
  const podeCorrigir = pode('autorizacoes.etapas.gerir');
  const [linhas, setLinhas] = useState<Atividade[]>([]);
  /* Os números das etapas ("2.0"), calculados pela MESMA função do card — dois
     números para a mesma etapa seria pior que nenhum. Só na visão do pedido:
     dentro de uma etapa não há o que numerar. */
  const [numeros, setNumeros] = useState<Map<number, string>>(new Map());
  const [carregando, setCarregando] = useState(true);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  /* A PROVA de cada registro. Vem da mesma coleção que a aba Documentos usa —
     cada documento já diz de qual atividade é —, então não há consulta nova. */
  const [docs, setDocs] = useState<Documento[]>([]);
  /* Os arquivos escolhidos ANTES de registrar: sobem depois, com o id da
     atividade, para que o relato seja a descrição deles. */
  const [anexos, setAnexos] = useState<File[]>([]);
  const [verHistorico, setVerHistorico] = useState(false);
  const campoArquivo = useRef<HTMLInputElement>(null);

  const caminho = etapaId
    ? `/autorizacoes/etapas/${etapaId}/atividades`
    : `/autorizacoes/${autorizacaoId}/atividades`;

  const caminhoDocs = etapaId
    ? `/autorizacoes/etapas/${etapaId}/documentos`
    : `/autorizacoes/${autorizacaoId}/documentos`;

  const carregar = useCallback(async () => {
    try {
      const [as, ds] = await Promise.all([
        api.get<Atividade[]>(caminho),
        api.get<Documento[]>(caminhoDocs).catch(() => [] as Documento[]),
      ]);
      setLinhas(as);
      setDocs(ds);
    } catch {
      /* silêncio: a lista vazia já diz o que precisa */
    } finally {
      setCarregando(false);
    }
  }, [caminho, caminhoDocs]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    if (etapaId) return;
    api.get<Etapa[]>(`/autorizacoes/${autorizacaoId}/etapas`)
      .then((es) => setNumeros(numerosDasEtapas(es)))
      .catch(() => undefined);
  }, [autorizacaoId, etapaId]);

  async function registrar() {
    const t = texto.trim();
    if (!t || enviando) return;
    setEnviando(true);
    try {
      /* PRIMEIRO o registro, DEPOIS os anexos — com o id dele. É o que amarra
         a prova ao que ela prova: um documento que chega sem esse vínculo é o
         "que porra de documento é esse?" que ele encontrou em 05/09. */
      const nova = await api.post<{ id: number }>(caminho, { relato: t });
      let falhas = 0;
      for (const arquivo of anexos) {
        const pronto = arquivo.type.startsWith('image/')
          ? await comprimirImagem(arquivo)
          : arquivo;
        const corpo = new FormData();
        corpo.append('arquivo', pronto);
        corpo.append('atividade_id', String(nova.id));
        if (etapaId) corpo.append('etapa_id', String(etapaId));
        try {
          await api.upload(`/autorizacoes/${autorizacaoId}/documentos`, corpo);
        } catch (err) {
          falhas += 1;
          toast.error(`${arquivo.name}: ${err instanceof ApiError ? err.message : 'não subiu.'}`);
        }
      }
      if (falhas === 0) setAnexos([]);
      setTexto('');
      carregar();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não deu para registrar.');
    } finally {
      setEnviando(false);
    }
  }

  /* Na visão do pedido, o campo só existe enquanto não há etapa nenhuma. */
  const semLugarParaRegistrar = !etapaId && numeros.size > 0;

  /* Os grupos, na ordem em que as etapas acontecem — o do PEDIDO primeiro. */
  /* ⛔ OS DOIS CONJUNTOS. `registros` é o que alguém escreveu — é o que a aba
     mostra. `historico` é o log do sistema, que continua guardado e sai só a
     pedido. Ver `eRegistroDeGente`, no topo do arquivo. */
  const registros = linhas.filter(eRegistroDeGente);
  const historico = linhas.filter((a) => !eRegistroDeGente(a));

  /* Os documentos de cada registro, casados pelo `atividade_id` que já vem na
     coleção — sem consulta nova por linha. */
  const docsDe = (id: number) => docs.filter((d) => d.atividade_id === id);

  const grupos = (() => {
    const porEtapa = new Map<number | null, Atividade[]>();
    registros.forEach((a) => {
      const k = a.etapa_id ?? null;
      porEtapa.set(k, [...(porEtapa.get(k) ?? []), a]);
    });
    const saida: { chave: string; numero: string | null; titulo: string; itens: Atividade[] }[] = [];
    const doPedido = porEtapa.get(null);
    if (doPedido) {
      saida.push({ chave: 'pedido', numero: null, titulo: 'O pedido', itens: doPedido });
    }
    [...porEtapa.entries()]
      .filter(([k]) => k !== null)
      .sort(([a1], [b1]) => (numeros.get(a1 as number) ?? '').localeCompare(numeros.get(b1 as number) ?? ''))
      .forEach(([k, itens]) => saida.push({
        chave: `etapa-${k}`,
        numero: numeros.get(k as number) ?? null,
        titulo: itens[0].etapa_titulo ?? 'Etapa',
        itens,
      }));
    return saida;
  })();

  if (carregando) {
    return (
      <div className="d-flex align-items-center gap-2 text-secondary small py-2">
        <span className="spinner-border spinner-border-sm" /> Carregando…
      </div>
    );
  }

  return (
    <div>
      {/* O CAMPO NO TOPO: quem abre isto costuma vir para contar o que fez, não
          para ler o que já sabe. E O BOTÃO FICA ABAIXO, não ao lado. Este componente vive na COLUNA da
          página da etapa, e num `input-group` estreito o campo era espremido a
          ponto de o texto de exemplo não caber ("O que aconteceu? — liguei, r").
          Medido em 05/09. Embaixo, o campo usa a largura inteira nos dois
          lugares onde o componente aparece. */}
      {podeEscrever && !semLugarParaRegistrar && (
        <div className="casaa-registro">
          <label htmlFor="atividade-relato" className="casaa-registro-titulo">
            <i className="bi bi-pencil-square me-1"></i>Registrar o que aconteceu
          </label>
          <textarea
            id="atividade-relato"
            className="form-control"
            rows={2}
            placeholder="Liguei para a fornecedora, não atenderam — retorno na terça."
            value={texto}
            onChange={(ev) => setTexto(ev.target.value)}
            onKeyDown={(ev) => {
              /* Enter envia, Shift+Enter quebra a linha — o mesmo do chat, e o
                 `isComposing` pelo mesmo motivo (acento morto e IME). */
              if (ev.key === 'Enter' && !ev.shiftKey && !ev.nativeEvent.isComposing) {
                ev.preventDefault();
                registrar();
              }
            }}
            maxLength={2000}
          />
          {/* OS ANEXOS DESTE REGISTRO, escolhidos antes de gravar. */}
          {anexos.length > 0 && (
            <div className="d-flex flex-wrap gap-2 mt-2">
              {anexos.map((f, i) => (
                <span key={`${f.name}-${i}`}
                      className="badge casaa-selo-neutro d-inline-flex align-items-center gap-1">
                  <i className={`bi ${f.type.startsWith('image/') ? 'bi-image' : 'bi-file-earmark-pdf'}`}></i>
                  <span className="text-truncate" style={{ maxWidth: '11rem' }}>{f.name}</span>
                  <button type="button" className="btn btn-link p-0 text-danger lh-1"
                          aria-label={`Tirar ${f.name}`}
                          onClick={() => setAnexos((arr) => arr.filter((_, k) => k !== i))}>
                    <i className="bi bi-x"></i>
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="casaa-registro-rodape">
            <span className="small text-secondary d-inline-flex align-items-center gap-2">
              <label className="btn btn-sm btn-outline-secondary mb-0">
                <i className="bi bi-paperclip me-1"></i>Anexar
                <input
                  ref={campoArquivo}
                  type="file"
                  className="d-none"
                  multiple
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  onChange={(ev) => {
                    setAnexos((arr) => [...arr, ...Array.from(ev.target.files ?? [])]);
                    if (campoArquivo.current) campoArquivo.current.value = '';
                  }}
                />
              </label>
              <span>
                <kbd>Enter</kbd> registra · <kbd>Shift</kbd>+<kbd>Enter</kbd> quebra a linha
              </span>
            </span>
            {/* O BOTÃO NÃO SE APAGA COM O CAMPO VAZIO — `disabled` o deixava
                cinza, parecendo quebrado; é a mesma lição do botão de enviar do
                chat. Quem não deixa registrar vazio é o `registrar()`. */}
            <button className="btn btn-theme btn-sm" type="button"
                    onClick={registrar} disabled={enviando}>
              {enviando
                ? <span className="spinner-border spinner-border-sm me-1" />
                : <i className="bi bi-send me-1"></i>}
              Registrar
            </button>
          </div>
        </div>
      )}

      {!etapaId && semLugarParaRegistrar && podeEscrever && (
        <p className="small text-secondary mb-3">
          <i className="bi bi-info-circle me-1"></i>
          Este pedido tem etapas — registre dentro da etapa a que o assunto
          pertence, e ele aparece aqui.
        </p>
      )}

      {registros.length === 0 ? (
        <p className="text-secondary small mb-0">
          Nada registrado ainda. O que se fizer por fora do sistema — ligação,
          e-mail, visita — vale mais escrito aqui do que lembrado depois.
        </p>
      ) : etapaId ? (
        /* Dentro de uma etapa não há o que agrupar: tudo é dela. */
        registros.map((a) => (
          <Linha key={a.id} a={a} docs={docsDe(a.id)} meuId={podeCorrigir ? meuId : null} aoMudar={carregar} />
        ))
      ) : (
        /* NA VISÃO DO PEDIDO, AGRUPADO POR ETAPA — pedido dele, 05/09:
           *"eu quero que esteja bem separado"*. Cada etapa abre o seu bloco,
           com número e título, e as atividades dela abaixo. O que é do próprio
           pedido (decisão, encerramento, o relato do caso simples) fica no
           grupo "O pedido", que vem primeiro por ser o começo de tudo.

           O preço, e é honesto dizê-lo: perde-se a leitura cronológica GLOBAL —
           "o que aconteceu neste caso, na ordem". Dentro de cada grupo a ordem
           continua sendo a do tempo. */
        grupos.map((g) => (
          <div className="mb-4" key={g.chave}>
            <div className="d-flex align-items-center gap-2 mb-2 pb-2 border-bottom">
              {g.numero ? (
                <span className="casaa-etapa-numero miudo bg-theme-1 text-white flex-shrink-0">
                  {g.numero}
                </span>
              ) : (
                <i className="bi bi-file-earmark-text text-secondary"></i>
              )}
              <h6 className="mb-0">{g.titulo}</h6>
              <span className="badge casaa-selo-neutro ms-auto">
                {g.itens.length} {g.itens.length === 1 ? 'atividade' : 'atividades'}
              </span>
            </div>
            {g.itens.map((a) => (
              <Linha key={a.id} a={a} docs={docsDe(a.id)} meuId={podeCorrigir ? meuId : null} aoMudar={carregar} />
            ))}
          </div>
        ))
      )}

      {/* O HISTÓRICO, ESCONDIDO — e ele existe. Dele, 05/09: *"se você quiser
          criar log, cria; agora, na hora que a pessoa for registrar atividade,
          você filtra o que vai exibir"*. A tabela é imutável e serve de
          auditoria: apagar o log seria perder a auditoria para ganhar uma tela
          limpa, e a tela fica limpa do mesmo jeito com o log recolhido. */}
      {historico.length > 0 && (
        <div className="mt-4 pt-3 border-top">
          <button type="button" className="btn btn-link btn-sm p-0 text-secondary"
                  onClick={() => setVerHistorico((v) => !v)}>
            <i className={`bi ${verHistorico ? 'bi-chevron-down' : 'bi-chevron-right'} me-1`}></i>
            Histórico do sistema ({historico.length})
          </button>
          {verHistorico && (
            <div className="mt-2">
              <p className="small text-secondary fst-italic mb-2">
                O que o sistema anotou sozinho — criação, início, edição. Fica
                aqui para auditoria; o que interessa no dia a dia está acima.
              </p>
              {historico.map((a) => <Linha key={a.id} a={a} historico />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
